import axios, { AxiosInstance } from 'axios';
import { 
  AuthResponse, 
  ChatRoomsResponse, 
  MessagesResponse, 
  ApiResponse,
  ChatRoom,
  Message,
  SendMessageRequest,
  ChatStats,
  ChatRoomResponse,
  EndUser,
  EndUserAuthResponse,
  MessageTemplate,
  TemplatesResponse,
  AccountResponse,
  AccountFormData,
  AdminUser
} from '../types';

class ApiClient {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || '/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // リクエストインターセプター（認証トークンを自動追加）
    this.client.interceptors.request.use((config) => {
      if (this.token) {
        (config.headers as any).Authorization = `Bearer ${this.token}`;
      }
      return config;
    });

    // レスポンスインターセプター（エラーハンドリング）
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // 認証エラーの場合、トークンをクリア
          console.error('🚨 認証エラー: トークンが無効です', error.config?.url);
          this.clearToken();
          // ログインページへリダイレクト（ループ防止）
          const loginPath = '/login';
          const currentPath = window.location.pathname;
          // ログインページ以外、かつ既にリダイレクト中でない場合のみリダイレクト
          if (currentPath !== loginPath && !sessionStorage.getItem('redirecting')) {
            sessionStorage.setItem('redirecting', 'true');
            setTimeout(() => {
              sessionStorage.removeItem('redirecting');
              window.location.replace(loginPath);
            }, 100);
          }
        }
        return Promise.reject(error);
      }
    );

    // 保存されたトークンを読み込み
    this.loadToken();
  }

  // トークン管理
  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('adminToken', token);
    localStorage.setItem('admin_token', token); // 互換性のため
  }

  clearToken(): void {
    this.token = null;
    localStorage.removeItem('adminToken');
    localStorage.removeItem('admin_token');
  }

  private loadToken(): void {
    const savedToken = localStorage.getItem('adminToken') || localStorage.getItem('admin_token');
    if (savedToken) {
      this.token = savedToken;
    }
  }

  getToken(): string | null {
    return this.token;
  }

  // 認証API
  async login(username: string, password: string): Promise<AuthResponse> {
    const response = await this.client.post<AuthResponse>('/auth/login', {
      username,
      password,
    });
    
    if (response.data.success && response.data.token) {
      this.setToken(response.data.token);
    }
    
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await this.client.post('/auth/logout');
    } finally {
      this.clearToken();
    }
  }

  async verifyToken(): Promise<ApiResponse> {
    const response = await this.client.get<ApiResponse>('/auth/verify');
    return response.data;
  }

  // チャットルーム管理API
  async getChatRooms(page = 1, limit = 1000, status?: string): Promise<ChatRoomsResponse> {
    const params: any = { page, limit };
    if (status && status !== 'all') {
      params.status = status;
    }
    const response = await this.client.get<ChatRoomsResponse>('/chatrooms', {
      params,
    });
    return response.data;
  }

  // オペレーター別のチャットルーム取得
  async getChatRoomsByOperator(operatorIdOrName: string, isName: boolean = false): Promise<ChatRoomsResponse> {
    const params: any = { page: 1, limit: 1000 };
    if (isName) {
      // 名前の場合、全件取得してフロントでフィルター
      const response = await this.client.get<ChatRoomsResponse>('/chatrooms', { params });
      const filtered = response.data.chatRooms?.filter(room => 
        room.created_by_name === operatorIdOrName
      ) || [];
      return {
        success: true,
        chatRooms: filtered,
        pagination: {
          page: 1,
          limit: 1000,
          hasMore: false
        }
      };
    } else {
      // IDの場合、createdByパラメータを使用
      params.createdBy = operatorIdOrName;
      const response = await this.client.get<ChatRoomsResponse>('/chatrooms', { params });
      return response.data;
    }
  }

  // タグ別のチャットルーム取得
  async getChatRoomsByTag(tagId: string): Promise<ChatRoomsResponse> {
    // 全件取得してフロントでフィルター
    const params: any = { page: 1, limit: 1000 };
    const response = await this.client.get<ChatRoomsResponse>('/chatrooms', { params });
    const filtered = response.data.chatRooms?.filter(room => {
      // custom_tagsが配列の場合
      if (room.custom_tags && Array.isArray(room.custom_tags)) {
        return room.custom_tags.includes(tagId);
      }
      return false;
    }) || [];
    return {
      success: true,
      chatRooms: filtered,
      pagination: {
        page: 1,
        limit: 1000,
        hasMore: false
      }
    };
  }

  async getChatRoom(roomId: string): Promise<ChatRoomResponse> {
    const response = await this.client.get<ChatRoomResponse>(`/chatrooms/${roomId}`);
    return response.data;
  }

  async createChatRoom(data: {
    title: string;
    description?: string;
    participantPhone?: string;
    participantEmail?: string;
    participantName: string;
    sendMethod?: 'sms' | 'email' | 'both';
    autoCloseEnabled?: boolean;
    autoCloseDays?: number;
  }): Promise<ApiResponse<ChatRoom>> {
    // ルーム作成者情報を付与（通知先特定のため）
    let createdByUsername: string | undefined;
    let createdByDisplayName: string | undefined;
    let createdByEmail: string | undefined;
    try {
      const adminUserStr = localStorage.getItem('adminUser');
      if (adminUserStr) {
        const adminUser = JSON.parse(adminUserStr || '{}');
        createdByUsername = adminUser?.username;
        createdByDisplayName = adminUser?.displayName;
        createdByEmail = adminUser?.email;
      }
    } catch {}

    const payload = {
      ...data,
      createdByUsername,
      createdByDisplayName,
      createdByEmail,
    } as any;

    const response = await this.client.post<ApiResponse<ChatRoom>>('/chatrooms', payload);
    return response.data;
  }

  async getChatStats(): Promise<ApiResponse<ChatStats>> {
    const response = await this.client.get<ApiResponse<ChatStats>>('/chatrooms/stats');
    return response.data;
  }

  // チャットルームメモ更新API
  async updateChatRoomNotes(roomId: string, notes: string): Promise<ApiResponse<any>> {
    const response = await this.client.put<ApiResponse<any>>(`/chatrooms/${roomId}/notes`, { notes });
    return response.data;
  }

  // チャットルームタグ更新API
  async updateChatRoomTags(roomId: string, tags: string[]): Promise<ApiResponse<any>> {
    const response = await this.client.put<ApiResponse<any>>(`/chatrooms/${roomId}/tags`, { tags });
    return response.data;
  }

  // チャットルーム案件No更新API
  async updateChatRoomProjectNo(roomId: string, projectNo: string): Promise<ApiResponse<any>> {
    const response = await this.client.put<ApiResponse<any>>(`/chatrooms/${roomId}/project-no`, { projectNo });
    return response.data;
  }

  // メッセージAPI
  async getMessages(roomId: string, opts?: { before?: string; after?: string; limit?: number }): Promise<MessagesResponse> {
    const params = {
      before: opts?.before,
      after: opts?.after,
      limit: opts?.limit ?? 5000,
    } as any;
    const response = await this.client.get(`/messages/${roomId}/messages`, { params });
    const raw = response.data as any;
    const messages = Array.isArray(raw?.data) ? raw.data : raw?.messages || [];
    return {
      success: !!raw?.success,
      messages,
      pagination: {
        page: 1,
        limit: params.limit,
        hasMore: !!raw?.paging?.hasMoreOlder,
        // 互換
        nextBefore: raw?.paging?.nextBefore,
      } as any,
    } as MessagesResponse;
  }

  async sendMessage(roomId: string, data: SendMessageRequest): Promise<ApiResponse<Message>> {
    const socketId = (window as any).CSCHAT_SOCKET_ID || '';
    
    // バックエンドが期待する形式に変換（スネークケース）
    const payload: any = {
      content: (data as any)?.content,
      message_type: (data as any)?.message_type || (data as any)?.messageType || ((data as any)?.file_url ? 'file' : 'text'),
    };
    
    // ファイル情報があれば追加
    if ((data as any)?.file_url) {
      payload.file_url = (data as any)?.file_url;
      payload.file_name = (data as any)?.file_name;
    }
    
    const response = await this.client.post<ApiResponse<Message>>(`/messages/${roomId}/messages`, payload, {
      headers: { 'X-Socket-Id': socketId }
    });
    return response.data;
  }

  async getUnreadCount(roomId: string): Promise<ApiResponse<{ count: number }>> {
    const response = await this.client.get<ApiResponse<{ count: number }>>(`/chatrooms/${roomId}/messages/unread-count`);
    return response.data;
  }

  async getBatchUnreadCounts(roomIds: string[]): Promise<{ success: boolean; counts: Record<string, number> }> {
    const response = await this.client.post<{ success: boolean; counts: Record<string, number> }>('/chatrooms/batch-unread-counts', { roomIds });
    return response.data;
  }

  async bulkUpdateRoomStatus(roomIds: string[], status: 'completed' | 'archived'): Promise<ApiResponse<{ successCount: number; failCount: number; total: number }>> {
    const response = await this.client.post<ApiResponse<{ successCount: number; failCount: number; total: number }>>('/chatrooms/bulk-status', { roomIds, status });
    return response.data;
  }

  // メッセージ編集
  async editMessage(roomId: string, messageId: string, content: string): Promise<ApiResponse<Message>> {
    const socketId = (window as any).CSCHAT_SOCKET_ID || '';
    const response = await this.client.put<ApiResponse<Message>>(`/messages/${roomId}/messages/${messageId}`, {
      content
    }, { headers: { 'X-Socket-Id': socketId } });
    return response.data;
  }

  // メッセージ削除
  async deleteMessage(roomId: string, messageId: string): Promise<ApiResponse> {
    const socketId = (window as any).CSCHAT_SOCKET_ID || '';
    const response = await this.client.delete<ApiResponse>(`/messages/${roomId}/messages/${messageId}`, {
      headers: { 'X-Socket-Id': socketId }
    });
    return response.data;
  }

  // 顧客API（管理画面用のみ - TODO: /api/admin/customersエンドポイント実装必要）
  async getCustomer(id: string): Promise<ApiResponse<any>> {
    // エンドユーザーはendUserApiClient.getChatRoomを使用
    const response = await this.client.get<ApiResponse<any>>(`/chatrooms/${id}`);
    return response.data;
  }

  async updateCustomer(id: string, data: any): Promise<ApiResponse<any>> {
    // 現在は未実装のため、成功レスポンスを返す（ダミー）
    return {
      success: true,
      data: { id, ...data },
      message: '顧客情報を更新しました'
    };
  }

  async updateCustomerTags(id: string, tags: string[]): Promise<ApiResponse<any>> {
    // 現在は未実装のため、成功レスポンスを返す（ダミー）
    return {
      success: true,
      data: { id, customTags: tags },
      message: 'タグを更新しました'
    };
  }

  async saveCustomerNotes(id: string, notes: string): Promise<ApiResponse<any>> {
    // 現在は未実装のため、成功レスポンスを返す（ダミー）
    return {
      success: true,
      data: { id, notes },
      message: 'メモを保存しました'
    };
  }

  // メッセージ既読
  async markMessageAsRead(roomId: string, messageId: string): Promise<ApiResponse<any>> {
    const response = await this.client.put<ApiResponse<any>>(`/messages/${roomId}/messages/${messageId}/read`);
    return response.data;
  }

  // 全メッセージ既読
  async markAllMessagesAsRead(roomId: string): Promise<ApiResponse<{ updatedCount: number }>> {
    const response = await this.client.put<ApiResponse<{ updatedCount: number }>>(`/messages/${roomId}/messages/mark-all-read`);
    return response.data;
  }

  // 招待URL再送機能
  async resendInvitation(roomId: string, email: string, participantName: string): Promise<ApiResponse<{ invitationUrl: string; otpCode: string; expiresAt: string }>> {
    const response = await this.client.post<ApiResponse<{ invitationUrl: string; otpCode: string; expiresAt: string }>>(`/chatrooms/${roomId}/resend-invitation`, {
      email,
      participantName
    });
    return response.data;
  }

  // 統計データ取得
  async getNavigationStats() {
    const response = await this.client.get('/stats/navigation');
    return response.data;
  }

  async getOperatorStats() {
    const response = await this.client.get('/stats/operators');
    return response.data;
  }

  async getTagStats() {
    const response = await this.client.get('/stats/tags');
    return response.data;
  }

  // 固定タグAPI（商材・対応部署）
  async getFixedTagCategories() {
    const response = await this.client.get('/fixed-tags/categories');
    return response.data;
  }

  async getFixedTagStats() {
    const response = await this.client.get('/fixed-tags/stats');
    return response.data;
  }

  async getBulkRoomFixedTags(roomIds: string[]) {
    const response = await this.client.post('/fixed-tags/bulk-room-tags', { room_ids: roomIds });
    return response.data;
  }

  async createFixedTagCategory(name: string, displayOrder?: number) {
    const response = await this.client.post('/fixed-tags/categories', { name, display_order: displayOrder });
    return response.data;
  }

  async updateFixedTagCategory(id: string, name: string, displayOrder?: number) {
    const response = await this.client.put(`/fixed-tags/categories/${id}`, { name, display_order: displayOrder });
    return response.data;
  }

  async deleteFixedTagCategory(id: string) {
    const response = await this.client.delete(`/fixed-tags/categories/${id}`);
    return response.data;
  }

  async createFixedTagOption(categoryId: string, name: string, displayOrder?: number) {
    const response = await this.client.post('/fixed-tags/options', { category_id: categoryId, name, display_order: displayOrder });
    return response.data;
  }

  async updateFixedTagOption(id: string, name: string, displayOrder?: number) {
    const response = await this.client.put(`/fixed-tags/options/${id}`, { name, display_order: displayOrder });
    return response.data;
  }

  async deleteFixedTagOption(id: string) {
    const response = await this.client.delete(`/fixed-tags/options/${id}`);
    return response.data;
  }

  async getChatRoomFixedTags(roomId: string) {
    const response = await this.client.get(`/chatrooms/${roomId}/fixed-tags`);
    return response.data;
  }

  async updateChatRoomFixedTags(roomId: string, tags: { category_id: string; option_id: string }[]) {
    const response = await this.client.put(`/chatrooms/${roomId}/fixed-tags`, { tags });
    return response.data;
  }

  // ルーム管理
  async updateRoomStatus(roomId: string, status: string, reason?: string) {
    const response = await this.client.put(`/chatrooms/${roomId}/status`, {
      status,
      reason
    });
    return response.data;
  }

  async completeRoom(roomId: string, reason?: string) {
    const response = await this.client.put(`/chatrooms/${roomId}/complete`, {
      reason
    });
    return response.data;
  }

  async reopenRoom(roomId: string, reason?: string) {
    const response = await this.client.put(`/chatrooms/${roomId}/reopen`, {
      reason
    });
    return response.data;
  }

  // ルームタグ
  async getRoomTags(roomId: string): Promise<ApiResponse<{ id: string; tags: string[] }>> {
    const response = await this.client.get(`/chatrooms/${roomId}/tags`);
    return response.data;
  }

  async updateRoomTags(roomId: string, tags: string[]): Promise<ApiResponse<{ id: string; tags: string[] }>> {
    const response = await this.client.put(`/chatrooms/${roomId}/tags`, { tags });
    return response.data;
  }

  // テンプレート機能
  async getTemplates(category?: string): Promise<TemplatesResponse> {
    const url = category ? `/messages/templates?category=${category}` : '/messages/templates';
    const response = await this.client.get<TemplatesResponse>(url);
    return response.data;
  }

  async createTemplate(data: Omit<MessageTemplate, 'id' | 'created_at' | 'usage_count'>): Promise<ApiResponse<MessageTemplate>> {
    const response = await this.client.post<ApiResponse<MessageTemplate>>('/messages/templates', data);
    return response.data as any;
  }

  async useTemplate(templateId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await this.client.post<ApiResponse<{ message: string }>>(`/messages/templates/${templateId}/use`);
    return response.data as any;
  }

  async updateTemplate(templateId: string, data: { title: string; content: string; category: string }): Promise<ApiResponse<MessageTemplate>> {
    const response = await this.client.put<ApiResponse<MessageTemplate>>(`/messages/templates/${templateId}`, data);
    return response.data as any;
  }

  async deleteTemplate(templateId: string): Promise<ApiResponse<void>> {
    const response = await this.client.delete<ApiResponse<void>>(`/messages/templates/${templateId}`);
    return response.data as any;
  }

  // アカウント管理機能
  async getAccounts(): Promise<AccountResponse> {
    const response = await this.client.get<AccountResponse>('/admin/accounts');
    return response.data;
  }

  async createAccount(data: AccountFormData): Promise<AccountResponse> {
    const response = await this.client.post<AccountResponse>('/admin/accounts', data);
    return response.data;
  }

  async updateAccount(id: string, data: Partial<AccountFormData>): Promise<AccountResponse> {
    const response = await this.client.put<AccountResponse>(`/admin/accounts/${id}`, data);
    return response.data;
  }

  async deleteAccount(id: string): Promise<AccountResponse> {
    const response = await this.client.delete<AccountResponse>(`/admin/accounts/${id}`);
    return response.data;
  }

  async getCurrentUser(): Promise<ApiResponse<AdminUser>> {
    const response = await this.client.get<ApiResponse<AdminUser>>('/admin/me');
    return response.data;
  }

  // ロック状態取得
  async getLocks(): Promise<{ success: boolean; locks: Record<string, { socketId: string; lockedAt: string }> }> {
    const response = await this.client.get('/locks');
    return response.data as any;
  }

  // ロック取得
  async acquireLock(roomId: string, socketId?: string): Promise<{ success: boolean; lock?: any; message?: string; lockedBy?: any }> {
    const response = await this.client.post(`/locks/${roomId}`, { socketId: socketId || '' });
    return response.data;
  }

  // ロック解放
  async releaseLock(roomId: string): Promise<{ success: boolean; message?: string }> {
    const response = await this.client.delete(`/locks/${roomId}`);
    return response.data;
  }

  // 強制ロック解除
  async forceReleaseLock(roomId: string): Promise<{ success: boolean; message?: string }> {
    const response = await this.client.delete(`/locks/${roomId}/force`);
    return response.data;
  }

  // ロック確認
  async checkLock(roomId: string): Promise<{ success: boolean; locked: boolean; lock: any | null }> {
    const response = await this.client.get(`/locks/${roomId}`);
    return response.data;
  }

  // 設定: 固定IPアドレス管理
  async getMyIp(): Promise<{ success: boolean; ip: string; forwarded?: any; realIp?: any }> {
    const response = await this.client.get('/admin/settings/allowed-ips/my-ip');
    return response.data;
  }

  async getAllowedIps(): Promise<{ success: boolean; enabled: boolean; items: { id: string; ip_address: string; description?: string; created_at?: string }[] }> {
    const response = await this.client.get('/admin/settings/allowed-ips');
    return response.data;
  }

  async addAllowedIp(ip: string, note?: string): Promise<{ success: boolean; item: { id: string; ip_address: string; description?: string; created_at?: string } }> {
    const response = await this.client.post('/admin/settings/allowed-ips', { 
      ip_address: ip, 
      description: note 
    });
    return response.data;
  }

  async removeAllowedIp(id: string): Promise<{ success: boolean; item?: any }> {
    const response = await this.client.delete(`/admin/settings/allowed-ips/${id}`);
    return response.data;
  }

  async setAllowedIpsEnabled(enabled: boolean): Promise<{ success: boolean; enabled: boolean }> {
    const response = await this.client.put('/admin/settings/allowed-ips/enabled', { enabled });
    return response.data;
  }

  // ファイルアップロード（将来実装）
  async uploadFile(file: File): Promise<ApiResponse<{ url: string; fileName?: string; originalName?: string }>> {
    // テスト環境ではbase64アップロードに統一
    // 事前サイズチェック（Nginx既定 10MB 想定 → 余裕を持って 9.5MB 未満に警告）
    const MAX_BYTES = 9.5 * 1024 * 1024; // 9.5MB
    let workingFile: File = file;
    if (workingFile.size > MAX_BYTES) {
      // 画像ならリサイズで軽量化を試みる
      const isImage = /^image\/(png|jpe?g|webp)$/i.test(workingFile.type);
      if (isImage) {
        try {
          const compressed = await new Promise<File>((resolve, reject) => {
            const img = new Image();
            const url = URL.createObjectURL(workingFile);
            img.onload = () => {
              const canvas = document.createElement('canvas');
              // 縦横の最大を2000pxに抑制しつつアスペクト比維持
              const maxSide = 2000;
              let { width, height } = img;
              if (width > height && width > maxSide) {
                height = Math.round((height * maxSide) / width);
                width = maxSide;
              } else if (height > maxSide) {
                width = Math.round((width * maxSide) / height);
                height = maxSide;
              }
              canvas.width = width;
              canvas.height = height;
              const ctx = canvas.getContext('2d');
              if (!ctx) return reject(new Error('Canvas context unavailable'));
              ctx.drawImage(img, 0, 0, width, height);
              canvas.toBlob(
                (blob) => {
                  if (!blob) return reject(new Error('Blob conversion failed'));
                  resolve(new File([blob], workingFile.name, { type: workingFile.type }));
                },
                workingFile.type,
                0.85 // 画質
              );
            };
            img.onerror = reject;
            img.src = url;
          });
          if (compressed.size < workingFile.size) {
            workingFile = compressed;
          }
        } catch (e) {
          console.warn('画像圧縮に失敗しました。元ファイルで送信します。', e);
        }
      }
      // まだ大きい場合はエラー
      if (workingFile.size > MAX_BYTES) {
        return { success: false, message: 'ファイルサイズが大きすぎます（上限約9.5MB）' } as any;
      }
    }

    const base64Data: string = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = reject;
      reader.readAsDataURL(workingFile);
    });
    const response = await this.client.post('/upload', {
      fileName: workingFile.name,
      contentType: workingFile.type,
      base64Data
    });
    return response.data;
  }

  // --- ヘルプ・マニュアル・FAQ API ---

  async getHelpArticles(params?: { type?: string; includeUnpublished?: boolean }) {
    try {
      const query = new URLSearchParams();
      if (params?.type) query.set('type', params.type);
      if (params?.includeUnpublished) query.set('includeUnpublished', 'true');
      const qs = query.toString();
      const response = await this.client.get(`/help${qs ? '?' + qs : ''}`);
      return response.data;
    } catch (error: any) {
      console.error('getHelpArticles error:', error);
      return { success: false, articles: [] };
    }
  }

  async createHelpArticle(article: any) {
    const response = await this.client.post('/help', article);
    return response.data;
  }

  async updateHelpArticle(id: string, article: any) {
    const response = await this.client.put(`/help/${id}`, article);
    return response.data;
  }

  async deleteHelpArticle(id: string) {
    const response = await this.client.delete(`/help/${id}`);
    return response.data;
  }

  async bulkHelpAction(body: { action: string; ids?: string[]; orders?: { id: string; sort_order: number }[] }) {
    const response = await this.client.post('/help/bulk', body);
    return response.data;
  }

  async seedHelpArticles(articles: any[]) {
    const response = await this.client.post('/help/seed', { articles });
    return response.data;
  }

  // レスポンスタイムレポートAPI
  async getResponseTimeLogs(params: { startDate?: string; endDate?: string; weekOf?: string }) {
    const response = await this.client.get('/response-time/logs', { params });
    return response.data;
  }

  async getWeeklyResponseReports() {
    const response = await this.client.get('/response-time/weekly');
    return response.data;
  }

  async downloadResponseTimeCSV(params: { startDate?: string; endDate?: string; weekOf?: string }) {
    const response = await this.client.get('/response-time/csv', {
      params,
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `response_time_${params.weekOf || params.startDate || 'all'}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  async downloadWeeklyResponseCSV(reportId: string) {
    const response = await this.client.get(`/response-time/weekly/${reportId}/csv`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `response_time_weekly_${reportId}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  async triggerWeeklyResponseReport(weekStart?: string) {
    const response = await this.client.post('/response-time/weekly/generate', { weekStart });
    return response.data;
  }

  async getFiles(params?: { page?: number; limit?: number; search?: string; roomId?: string; dateFrom?: string; dateTo?: string }) {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.search) query.set('search', params.search);
    if (params?.roomId) query.set('roomId', params.roomId);
    if (params?.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params?.dateTo) query.set('dateTo', params.dateTo);
    const qs = query.toString();
    const response = await this.client.get(`/files${qs ? '?' + qs : ''}`);
    return response.data;
  }

  // 外部APIキー管理
  async getExternalApiKeys() {
    const response = await this.client.get('/admin/external-api-keys');
    return response.data;
  }

  async createExternalApiKey(data: {
    key_name: string;
    description?: string;
    allowed_ips?: string[];
    permissions?: string[];
    rate_limit_per_minute?: number;
    expires_in_days?: number;
  }) {
    const response = await this.client.post('/admin/external-api-keys', data);
    return response.data;
  }

  async updateExternalApiKey(id: string, data: {
    key_name?: string;
    description?: string;
    allowed_ips?: string[];
    permissions?: string[];
    rate_limit_per_minute?: number;
    is_active?: boolean;
  }) {
    const response = await this.client.put(`/admin/external-api-keys/${id}`, data);
    return response.data;
  }

  async deleteExternalApiKey(id: string) {
    const response = await this.client.delete(`/admin/external-api-keys/${id}`);
    return response.data;
  }

  async regenerateExternalApiSecret(id: string) {
    const response = await this.client.post(`/admin/external-api-keys/${id}/regenerate-secret`);
    return response.data;
  }

  async getExternalApiKeyLogs(id: string, params?: { page?: number; limit?: number }) {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    const response = await this.client.get(`/admin/external-api-keys/${id}/logs${qs ? '?' + qs : ''}`);
    return response.data;
  }
}

// シングルトンインスタンス
const apiClient = new ApiClient();

// エンドユーザー用APIクライアント
class EndUserApiClient {
  private client: AxiosInstance;
  private sessionToken: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || '/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // リクエストインターセプター（セッショントークンを自動追加）
    this.client.interceptors.request.use((config) => {
      if (this.sessionToken) {
        config.headers.Authorization = `Bearer ${this.sessionToken}`;
      }
      return config;
    });

    // レスポンスインターセプター
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // 開発環境で認証スキップする場合はリダイレクトしない
          const skipAuth = process.env.REACT_APP_SKIP_ENDUSER_AUTH === 'true';
          if (!skipAuth) {
            this.clearSession();
            window.location.href = '/user/login';
          }
        }
        return Promise.reject(error);
      }
    );

    this.loadSession();
  }

  // セッション管理
  setSession(token: string, user: EndUser | null): void {
    this.sessionToken = token;
    localStorage.setItem('endUserToken', token);
    if (user) {
      localStorage.setItem('endUser', JSON.stringify(user));
    }
  }

  clearSession(): void {
    this.sessionToken = null;
    localStorage.removeItem('endUserToken');
    localStorage.removeItem('endUser');
  }

  private loadSession(): void {
    const savedToken = localStorage.getItem('endUserToken');
    if (savedToken) {
      this.sessionToken = savedToken;
    }
  }

  getSession(): { token: string | null; user: EndUser | null } {
    const token = this.sessionToken;
    const userStr = localStorage.getItem('endUser');
    const user = userStr ? JSON.parse(userStr) : null;
    return { token, user };
  }

  // 簡易ログイン（SMS認証なし）
  async loginWithPhone(phoneNumber: string, displayName: string): Promise<EndUserAuthResponse> {
    try {
      // バックエンドAPIを呼び出し（簡易版）
      const response = await this.client.post<EndUserAuthResponse>('/enduser/simple-login', {
        phoneNumber,
        displayName
      });

      if (response.data.success && response.data.user && response.data.sessionToken) {
        this.setSession(response.data.sessionToken, response.data.user);
      }

      return response.data;
    } catch (error) {
      console.error('エンドユーザーログインエラー:', error);
      throw error;
    }
  }

  // チャットルーム詳細取得
  async getChatRoom(roomId: string): Promise<ChatRoomResponse> {
    const response = await this.client.get<ChatRoomResponse>(`/enduser/chatrooms/${roomId}`);
    return response.data;
  }

  // メッセージ取得
  async getMessages(roomId: string, opts?: { before?: string; after?: string; limit?: number }): Promise<MessagesResponse> {
    const params = {
      before: opts?.before,
      after: opts?.after,
      limit: opts?.limit ?? 5000,
    } as any;
    const response = await this.client.get<MessagesResponse>(`/enduser/chatrooms/${roomId}/messages`, { params });
    return response.data as any;
  }

  // メッセージ送信
  async sendMessage(roomId: string, content: string, fileMeta?: { url: string; fileName?: string; originalName?: string }): Promise<ApiResponse<Message>> {
    // セッションからユーザー情報を取得
    const { user } = this.getSession();
    const senderName = user?.displayName || 'お客様';
    
    const data: any = {
      content,
      sender_name: senderName,
      sender_type: 'end_user',
      message_type: fileMeta ? 'file' : 'text',
    };
    if (fileMeta) {
      data.file_url = fileMeta.url;
      data.file_name = fileMeta.originalName || fileMeta.fileName;
    }
    const response = await this.client.post<ApiResponse<Message>>(`/enduser/chatrooms/${roomId}/messages`, data);
    return response.data;
  }

  // 未読数取得
  async getUnreadCount(roomId: string): Promise<ApiResponse<{ count: number }>> {
    const response = await this.client.get<ApiResponse<{ count: number }>>(`/enduser/chatrooms/${roomId}/messages/unread-count`);
    return response.data;
  }

  // メッセージ編集
  async editMessage(roomId: string, messageId: string, content: string): Promise<ApiResponse<Message>> {
    const data = { content };
    const response = await this.client.put<ApiResponse<Message>>(`/enduser/chatrooms/${roomId}/messages/${messageId}`, data);
    return response.data;
  }

  // メッセージ削除
  async deleteMessage(roomId: string, messageId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await this.client.delete<ApiResponse<{ message: string }>>(`/enduser/chatrooms/${roomId}/messages/${messageId}`);
    return response.data;
  }

  // ファイルアップロード
  async uploadFile(file: File): Promise<ApiResponse<{ url: string; fileName?: string; originalName?: string }>> {
    // 事前サイズチェック（Nginx既定 10MB 想定 → 余裕を持って 9.5MB 未満に警告）
    const MAX_BYTES = 9.5 * 1024 * 1024; // 9.5MB
    let workingFile: File = file;
    
    if (workingFile.size > MAX_BYTES) {
      // 画像ならリサイズで軽量化を試みる
      const isImage = /^image\/(png|jpe?g|webp)$/i.test(workingFile.type);
      if (isImage) {
        try {
          const compressed = await new Promise<File>((resolve, reject) => {
            const img = new Image();
            const url = URL.createObjectURL(workingFile);
            img.onload = () => {
              const canvas = document.createElement('canvas');
              // 縦横の最大を2000pxに抑制しつつアスペクト比維持
              const maxSide = 2000;
              let { width, height } = img;
              if (width > height && width > maxSide) {
                height = Math.round((height * maxSide) / width);
                width = maxSide;
              } else if (height > maxSide) {
                width = Math.round((width * maxSide) / height);
                height = maxSide;
              }
              canvas.width = width;
              canvas.height = height;
              const ctx = canvas.getContext('2d');
              if (!ctx) return reject(new Error('Canvas context unavailable'));
              ctx.drawImage(img, 0, 0, width, height);
              canvas.toBlob(
                (blob) => {
                  if (!blob) return reject(new Error('Blob conversion failed'));
                  resolve(new File([blob], workingFile.name, { type: workingFile.type }));
                },
                workingFile.type,
                0.85 // 画質
              );
            };
            img.onerror = reject;
            img.src = url;
          });
          if (compressed.size < workingFile.size) {
            workingFile = compressed;
            console.log(`✅ 画像を圧縮しました: ${(file.size / 1024).toFixed(1)}KB → ${(workingFile.size / 1024).toFixed(1)}KB`);
          }
        } catch (e) {
          console.warn('画像圧縮に失敗しました。元ファイルで送信します。', e);
        }
      }
      
      // まだ大きい場合はエラー
      if (workingFile.size > MAX_BYTES) {
        throw new Error(`ファイルサイズが大きすぎます。9.5MB以下にしてください。\n現在のサイズ: ${(workingFile.size / 1024 / 1024).toFixed(2)}MB`);
      }
    }

    // base64エンコード
    const base64Data: string = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = reject;
      reader.readAsDataURL(workingFile);
    });
    
    // アップロード実行
    const response = await this.client.post('/upload', {
      fileName: workingFile.name,
      contentType: workingFile.type,
      base64Data
    });
    
    return response.data;
  }
}

// エンドユーザー用APIクライアントのシングルトンインスタンス
const endUserApiClient = new EndUserApiClient();

// アカウント管理API（モック削除）
const mockAccounts: AdminUser[] = [];

// 実際のAPIエンドポイントを使用（フォールバック付き）
export const getAccounts = async (): Promise<{ accounts: AdminUser[] }> => {
  try {
    const response = await apiClient.getAccounts();
    return { accounts: response.accounts || [] };
  } catch (error) {
    // モックにフォールバックしない（常にエラーを投げる）
    throw error as any;
  }
};

export const createAccount = async (data: AccountFormData): Promise<AccountResponse> => {
  try {
    const response = await apiClient.createAccount(data);
    return response;
  } catch (error) {
    throw error as any;
  }
};

export const updateAccount = async (id: string, data: Partial<AccountFormData>): Promise<AccountResponse> => {
  try {
    const response = await apiClient.updateAccount(id, data);
    return response;
  } catch (error) {
    throw error as any;
  }
};

export const deleteAccount = async (id: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await apiClient.deleteAccount(id);
    return {
      success: response.success || false,
      message: response.message || 'アカウントを削除しました'
    };
  } catch (error) {
    throw error as any;
  }
};

export const getCurrentUser = async (): Promise<{ success: boolean; data: AdminUser }> => {
  try {
    const response = await apiClient.getCurrentUser();
    return {
      success: response.success || false,
      data: response.data || mockAccounts[0]
    };
  } catch (error) {
    throw error as any;
  }
};

// ===============================
// 分析API関数
// ===============================

// API認証ヘッダー生成
const getHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

// ファネル分析（開封率・到達率レポート）
export const getFunnelAnalytics = async (startDate?: string, endDate?: string) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await fetch(`/api/reports/funnel-analytics?${params}`, {
      headers: getHeaders()
    });
    return await response.json();
  } catch (error) {
    console.warn('Funnel analytics API error:', error);
    return { success: false, message: 'ファネル分析の取得に失敗しました' };
  }
};

// 分析統計概要取得
export const getAnalyticsOverview = async () => {
  try {
    const response = await fetch('/api/admin/analytics/overview', {
      headers: getHeaders()
    });
    return await response.json();
  } catch (error) {
    console.warn('Analytics overview API error, using mock data:', error);
    return {
      success: true,
      data: {
        messageStats: {
          total: 248,
          today: 12,
          week: 78,
          month: 186,
          growth: { daily: 1.2, weekly: 1.15, monthly: 0.92 }
        },
        roomStats: {
          total: 45,
          active: 12,
          closed: 33,
          averageMessages: 5.5
        },
        responseTimeStats: {
          average: 8.5,
          median: 6.2,
          fastest: 0.8,
          slowest: 45.3,
          unit: 'minutes'
        },
        satisfactionStats: {
          average: 4.2,
          total_responses: 85,
          distribution: { 5: 45, 4: 25, 3: 10, 2: 3, 1: 2 }
        }
      }
    };
  }
};

// 時系列分析データ取得
export const getAnalyticsTimeline = async (period: string = '7d') => {
  try {
    const response = await fetch(`/api/admin/analytics/timeline?period=${period}`, {
      headers: getHeaders()
    });
    return await response.json();
  } catch (error) {
    console.warn('Analytics timeline API error, using mock data:', error);
    const mockTimeline = [];
    const days = period === '30d' ? 30 : period === '90d' ? 90 : 7;
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
      mockTimeline.push({
        date: date.toISOString().split('T')[0],
        totalMessages: Math.floor(Math.random() * 20 + 5),
        adminMessages: Math.floor(Math.random() * 12 + 2),
        userMessages: Math.floor(Math.random() * 15 + 3),
        newRooms: Math.floor(Math.random() * 5),
        closedRooms: Math.floor(Math.random() * 3)
      });
    }
    
    return {
      success: true,
      data: { period, timeline: mockTimeline }
    };
  }
};

// カテゴリ別分析取得
export const getAnalyticsCategories = async () => {
  try {
    const response = await fetch('/api/admin/analytics/categories', {
      headers: getHeaders()
    });
    return await response.json();
  } catch (error) {
    console.warn('Analytics categories API error, using mock data:', error);
    return {
      success: true,
      data: {
        totalQueries: 140,
        categories: [
          { name: '商品に関する問い合わせ', count: 45, percentage: 32.1, avgResponseTime: 7.2 },
          { name: '料金・請求について', count: 38, percentage: 27.1, avgResponseTime: 5.8 },
          { name: '技術サポート', count: 28, percentage: 20.0, avgResponseTime: 12.5 },
          { name: 'アカウント・ログイン', count: 15, percentage: 10.7, avgResponseTime: 4.3 },
          { name: 'その他', count: 14, percentage: 10.0, avgResponseTime: 6.1 }
        ],
        trends: {
          growing: ['商品に関する問い合わせ', '技術サポート'],
          declining: ['アカウント・ログイン'],
          stable: ['料金・請求について', 'その他']
        }
      }
    };
  }
};

// パフォーマンス分析取得
export const getAnalyticsPerformance = async () => {
  try {
    const response = await fetch('/api/admin/analytics/performance', {
      headers: getHeaders()
    });
    return await response.json();
  } catch (error) {
    console.warn('Analytics performance API error, using mock data:', error);
    return {
      success: true,
      data: {
        totalStaff: 3,
        performance: [
          {
            id: 'admin-001',
            name: '管理者',
            role: 'admin',
            handledMessages: 85,
            averageResponseTime: 7.2,
            satisfactionScore: 4.5,
            activeRooms: 5,
            completedRooms: 23,
            workingHours: 42.5
          },
          {
            id: 'operator-001',
            name: '運営者',
            role: 'operator',
            handledMessages: 67,
            averageResponseTime: 9.1,
            satisfactionScore: 4.2,
            activeRooms: 4,
            completedRooms: 19,
            workingHours: 38.0
          },
          {
            id: 'operator-002',
            name: '山田太郎',
            role: 'operator',
            handledMessages: 52,
            averageResponseTime: 8.7,
            satisfactionScore: 4.3,
            activeRooms: 3,
            completedRooms: 15,
            workingHours: 35.5
          }
        ],
        teamAverages: {
          responseTime: 8.3,
          satisfaction: 4.3,
          messagesPerStaff: 68
        }
      }
    };
  }
}

// エクスポート機能
export const exportAnalyticsData = async (format: string = 'csv', period: string = '30d') => {
  try {
    const response = await fetch(`/api/admin/analytics/export?format=${format}&period=${period}`, {
      headers: getHeaders()
    });
    
    if (format === 'csv') {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-export-${period}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      return { success: true, message: 'CSVファイルをダウンロードしました' };
    } else {
      return await response.json();
    }
  } catch (error) {
    console.warn('Export API error:', error);
    return { success: false, message: 'エクスポートに失敗しました' };
  }
};

export default apiClient;
export { endUserApiClient };